/**
 * useInitialHydrate — one-time pull-from-server hydrate on authentication.
 *
 * A fresh browser only ever reads local IndexedDB, so without this a new device
 * shows nothing until the user re-creates data. This hook fetches the user's
 * server-side data once per auth session (when online) and upserts it into the
 * Dexie tables WITHOUT clobbering newer / pending local changes.
 *
 * Version guard: a server item only overwrites a local item when there is no
 * local item OR the server version is strictly newer. A local entry with
 * unsynced (Pending) changes is never overwritten — the sync queue will push it.
 *
 * Journal is the priority; tasks/habits/notes are each guarded independently so
 * one failing endpoint never aborts the others. Nothing here ever throws to the
 * UI — failures are swallowed with at most a console.warn.
 *
 * @see Requirements 8.1, 8.4, 12.6
 */

import { useEffect, useRef } from 'react';
import { getIdToken } from '@/services/auth';
import { useAuthStore } from '@/stores/authStore';
import { db, getEntry, putEntry } from '@/services/localDb';
import { SyncStatus } from '@/types/enums';
import type { JournalEntry, Task, Habit, Note } from '@/types';

// ─── Constants ───────────────────────────────────────────────────────────────

const API_BASE_URL =
  import.meta.env.VITE_API_URL ?? import.meta.env.VITE_API_BASE_URL ?? '/api';

// ─── Fetch helper ──────────────────────────────────────────────────────────────

/** GET a collection endpoint and return the parsed JSON, or null on any failure. */
async function fetchJson(path: string): Promise<Record<string, unknown> | null> {
  const token = await getIdToken();
  if (!token) return null;

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    return null;
  }
  const text = await res.text();
  return text ? (JSON.parse(text) as Record<string, unknown>) : {};
}

// ─── Per-module hydration ───────────────────────────────────────────────────────

/**
 * Hydrate journal entries. For each server entry: write it locally only if there
 * is no local entry for that date OR the server version is strictly newer. Never
 * overwrite a local entry that has pending (unsynced) changes.
 */
export async function hydrateJournal(): Promise<void> {
  try {
    const data = await fetchJson('/entries');
    if (!data) return;

    const entries = (data.entries as JournalEntry[] | undefined) ?? [];
    for (const server of entries) {
      if (!server || typeof server.entryDate !== 'string') continue;

      const local = await getEntry(server.entryDate);

      // Preserve local pending edits — the queue will push them.
      if (local && local.syncStatus === SyncStatus.Pending) {
        continue;
      }

      // Only write when missing locally or server is strictly newer.
      if (!local || (server.version ?? 0) > (local.version ?? 0)) {
        await putEntry({
          entryDate: server.entryDate,
          content: server.content ?? '',
          version: server.version ?? 0,
          localVersion: local?.localVersion ?? 0,
          updatedAt: server.updatedAt ?? new Date().toISOString(),
          createdAt: server.createdAt ?? local?.createdAt ?? new Date().toISOString(),
          mediaRefs: server.mediaRefs ?? [],
          syncStatus: SyncStatus.Synced,
          contentHash: server.contentHash ?? local?.contentHash ?? '',
          pendingMediaUploads: local?.pendingMediaUploads ?? [],
        });
      }
    }
  } catch (err) {
    console.warn('Journal hydrate failed', err);
  }
}

/** Upsert server tasks into Dexie, guarding on updatedAt (string ISO) freshness. */
export async function hydrateTasks(): Promise<void> {
  try {
    const data = await fetchJson('/tasks');
    if (!data) return;

    const tasks = (data.tasks as Task[] | undefined) ?? [];

    // Dedupe by natural key (title): the sync queue pushes local items and the
    // server assigns NEW ids, so matching on id alone would re-insert a copy of
    // an item that already exists locally. Skip any server task whose title is
    // already present locally, keeping whatever copy was there first.
    const localTasks = await db.tasks.toArray();
    const localTitles = new Set(localTasks.map((t) => t.title));

    for (const server of tasks) {
      if (!server || typeof server.taskId !== 'string') continue;
      const local = await db.tasks.get(server.taskId);
      if (!local && localTitles.has(server.title)) continue;
      if (local && local.syncStatus === SyncStatus.Pending) continue;
      if (!local || (server.updatedAt ?? '') > (local.updatedAt ?? '')) {
        await db.tasks.put({ ...server, syncStatus: SyncStatus.Synced });
      }
    }
  } catch (err) {
    console.warn('Tasks hydrate failed', err);
  }
}

/** Upsert server habits into Dexie, guarding on updatedAt freshness. */
export async function hydrateHabits(): Promise<void> {
  try {
    const data = await fetchJson('/habits');
    if (!data) return;

    const habits = (data.habits as Habit[] | undefined) ?? [];

    // Dedupe by natural key (name) — see hydrateTasks for the rationale.
    const localHabits = await db.habits.toArray();
    const localNames = new Set(localHabits.map((h) => h.name));

    for (const server of habits) {
      if (!server || typeof server.habitId !== 'string') continue;
      const local = await db.habits.get(server.habitId);
      if (!local && localNames.has(server.name)) continue;
      if (local && local.syncStatus === SyncStatus.Pending) continue;
      if (!local || (server.updatedAt ?? '') > (local.updatedAt ?? '')) {
        await db.habits.put({ ...server, syncStatus: SyncStatus.Synced });
      }
    }
  } catch (err) {
    console.warn('Habits hydrate failed', err);
  }
}

/** Upsert server notes into Dexie, guarding on updatedAt freshness. */
export async function hydrateNotes(): Promise<void> {
  try {
    const data = await fetchJson('/notes');
    if (!data) return;

    const notes = (data.notes as Note[] | undefined) ?? [];

    // Dedupe by natural key (title) — see hydrateTasks for the rationale.
    const localNotes = await db.notes.toArray();
    const localTitles = new Set(localNotes.map((n) => n.title));

    for (const server of notes) {
      if (!server || typeof server.noteId !== 'string') continue;
      const local = await db.notes.get(server.noteId);
      if (!local && localTitles.has(server.title)) continue;
      if (local && local.syncStatus === SyncStatus.Pending) continue;
      if (!local || (server.updatedAt ?? '') > (local.updatedAt ?? '')) {
        await db.notes.put({ ...server, syncStatus: SyncStatus.Synced });
      }
    }
  } catch (err) {
    console.warn('Notes hydrate failed', err);
  }
}

/**
 * Run a one-time hydrate across all modules. Journal first (priority); the rest
 * are independent and guarded so one failure never aborts the others.
 */
export async function runInitialHydrate(): Promise<void> {
  await hydrateJournal();
  await Promise.allSettled([hydrateTasks(), hydrateHabits(), hydrateNotes()]);
}

// ─── Hook ───────────────────────────────────────────────────────────────────

/**
 * Pulls the user's server data into IndexedDB once per auth session when online.
 * Mount once in a top-level component (e.g. AppShell), after the sync queue flush
 * is kicked off. The version/pending guard ensures a pull never overwrites a
 * pending local change.
 */
export function useInitialHydrate(): void {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasHydratedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || hasHydratedRef.current) {
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return;
    }

    hasHydratedRef.current = true;
    void runInitialHydrate();
  }, [isAuthenticated]);
}
